import { Injectable, signal } from '@angular/core';

const VOICE_KEY = 'nexvenue.speech.voiceUri';

/** How long to wait for a browser that populates getVoices() asynchronously. */
const VOICE_WAIT_MS = 1_000;

/**
 * Name fragments that identify a female en-US voice, best first, across the
 * engines staff devices actually ship: macOS/iOS (Samantha, Ava, Allison,
 * Susan), Windows (Zira, Aria, Michelle, Jenny), Chrome ("Google US
 * English" is female), Android (…female_en-US). Matching on names is
 * unavoidable — the Web Speech API exposes no gender attribute.
 */
const FEMALE_US_VOICE_HINTS = [
  'samantha',
  'zira',
  'aria',
  'jenny',
  'michelle',
  'ava',
  'allison',
  'susan',
  'joanna',
  'salli',
  'kimberly',
  'nicky',
  'google us english',
  'female',
];

/**
 * Spoken announcements for guest requests ("Water bottle for table 16").
 *
 * Strictly an enhancement layered over AlarmService: the recurring tune is
 * what actually alerts the floor, so every failure path here — no
 * speechSynthesis, no English voice, a browser that rejects the utterance —
 * is a silent no-op rather than an error the caller has to handle.
 *
 * Voice choice is deliberately isolated in pickVoice()/setPreferredVoice():
 * Venue Settings will eventually persist a chosen voice per venue, and only
 * needs to write the preferred voiceURI for the heuristic below to step aside.
 */
@Injectable({ providedIn: 'root' })
export class SpeechService {
  private readonly synth: SpeechSynthesis | null =
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    typeof SpeechSynthesisUtterance !== 'undefined'
      ? window.speechSynthesis
      : null;

  readonly supported = this.synth !== null;

  /** Interim store until Venue Settings owns this — see setPreferredVoice(). */
  private readonly preferredVoiceUri = signal<string | null>(this.readPreferred());
  readonly preferredVoice = this.preferredVoiceUri.asReadonly();

  /** Utterances we queued ourselves, so leftover speech can be told apart from ours. */
  private outstanding = 0;
  /** Chrome garbage-collects utterances mid-sentence unless a reference is held. */
  private readonly live = new Set<SpeechSynthesisUtterance>();
  private voicesPromise: Promise<SpeechSynthesisVoice[]> | null = null;

  /**
   * Speak once. Callers are responsible for not announcing the same request
   * twice. Resolves once the utterance has finished (or immediately if
   * there is nothing to say) — RequestAlerts awaits this to know when a
   * round's tune should start fading, so the fade never cuts speech off.
   */
  async announce(text: string): Promise<void> {
    if (!this.synth || !text.trim()) return;
    const voices = await this.loadVoices();
    // Some mobile engines speak fine but never expose a voice list; an
    // empty list is not evidence that speech is unavailable.
    if (voices.length === 0) {
      await this.speak(text, null);
      return;
    }
    const voice = this.pickVoice(voices);
    // Nothing English installed — a Spanish engine reading English words
    // is worse than saying nothing. The tune still carries the alert.
    if (voice) await this.speak(text, voice);
  }

  /** Drop anything queued or in progress (mute, teardown). */
  cancel(): void {
    if (!this.synth) return;
    try {
      this.synth.cancel();
    } catch {
      // Nothing to clean up if the engine is already gone.
    }
    this.outstanding = 0;
    this.live.clear();
  }

  /** English voices this browser offers — the option list for a future Venue Settings picker. */
  async listVoices(): Promise<SpeechSynthesisVoice[]> {
    const voices = await this.loadVoices();
    return voices.filter((voice) => voice.lang?.toLowerCase().startsWith('en'));
  }

  /** Venue Settings entry point: pin a voice by voiceURI, or null to fall back to the heuristic. */
  setPreferredVoice(voiceUri: string | null): void {
    this.preferredVoiceUri.set(voiceUri);
    try {
      if (voiceUri) localStorage.setItem(VOICE_KEY, voiceUri);
      else localStorage.removeItem(VOICE_KEY);
    } catch {
      // Storage disabled — the choice just won't survive a reload.
    }
  }

  private pickVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
    const preferred = this.preferredVoiceUri();
    if (preferred) {
      const configured = voices.find((voice) => voice.voiceURI === preferred);
      if (configured) return configured;
    }

    const langOf = (voice: SpeechSynthesisVoice) => voice.lang?.toLowerCase().replace('_', '-') ?? '';
    const us = voices.filter((voice) => langOf(voice).startsWith('en-us'));
    const pool = us.length ? us : voices.filter((voice) => langOf(voice).startsWith('en'));
    if (!pool.length) return null;

    for (const hint of FEMALE_US_VOICE_HINTS) {
      const match = pool.find((voice) => voice.name.toLowerCase().includes(hint));
      if (match) return match;
    }
    // English, but the engine gives us nothing to judge gender by — still
    // better than silence.
    return pool[0];
  }

  private speak(text: string, voice: SpeechSynthesisVoice | null): Promise<void> {
    const synth = this.synth;
    if (!synth) return Promise.resolve();
    try {
      // Speech left over from a previous page state (a tab restored
      // mid-utterance, a cancelled navigation) would hold this announcement
      // in the queue behind something nobody is waiting for.
      if (this.outstanding === 0 && (synth.speaking || synth.pending)) synth.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = voice?.lang ?? 'en-US';
      if (voice) utterance.voice = voice;

      return new Promise<void>((resolve) => {
        const settle = () => {
          this.outstanding = Math.max(0, this.outstanding - 1);
          this.live.delete(utterance);
          resolve();
        };
        utterance.addEventListener('end', settle);
        // Blocked by the browser, interrupted, engine failure — the tune is
        // still ringing, so there is nothing to report. Still resolves:
        // callers await this to know when it's safe to fade the tune, and
        // a failed utterance is exactly as "done" as a finished one for
        // that purpose.
        utterance.addEventListener('error', settle);

        this.outstanding += 1;
        this.live.add(utterance);
        synth.speak(utterance);
      });
    } catch {
      // Enhancement only.
      return Promise.resolve();
    }
  }

  /**
   * Firefox and Chrome populate getVoices() asynchronously and fire
   * 'voiceschanged' when they are ready. One shared promise covers a burst
   * of requests, so a cluster of arrivals can't attach a listener each.
   */
  private loadVoices(): Promise<SpeechSynthesisVoice[]> {
    const synth = this.synth;
    if (!synth) return Promise.resolve([]);

    const ready = this.safeGetVoices(synth);
    if (ready.length) return Promise.resolve(ready);

    this.voicesPromise ??= new Promise<SpeechSynthesisVoice[]>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try {
          synth.removeEventListener('voiceschanged', finish);
        } catch {
          // Listener was never attached.
        }
        // Cleared so a later call re-checks rather than caching "no voices"
        // for the life of the tab.
        this.voicesPromise = null;
        resolve(this.safeGetVoices(synth));
      };

      // Announce with the default voice rather than waiting forever on an
      // engine that never fires the event.
      const timer = setTimeout(finish, VOICE_WAIT_MS);
      try {
        synth.addEventListener('voiceschanged', finish);
      } catch {
        // Old engines only expose onvoiceschanged; the timeout covers it.
      }
    });
    return this.voicesPromise;
  }

  private safeGetVoices(synth: SpeechSynthesis): SpeechSynthesisVoice[] {
    try {
      return synth.getVoices() ?? [];
    } catch {
      return [];
    }
  }

  private readPreferred(): string | null {
    try {
      return localStorage.getItem(VOICE_KEY);
    } catch {
      return null;
    }
  }
}
